import classNames from "classnames";
import serializeNode from "dom-serializer";
import HtmlToReact, { Parser } from "html-to-react";
import { produce } from "immer";
import find from "lodash/find";
import includes from "lodash/includes";
import pull from "lodash/pull";
import React, { useContext, useEffect } from "react";

import { ContentStyles } from "@examspark/common-ui";
import Math from "@examspark/common-ui/dist/components/Math";
import { Stem, StemTextSelectable } from "@examspark/types";

import QuestionContext from "../QuestionContext";

import Prompt from "./shared/Prompt";

type TextProps = {
  key: React.Key;
  data: Stem.Option | undefined;
  mark?: string;
  gapId: string;
  isSelected?: boolean;
  onTextClick: () => void;
};
const Text = (props: TextProps) => {
  const text = props.data?.body ?? "";
  return (
    <span
      onClick={props.onTextClick}
      className={classNames({
        "item-player-hottext": true,
        "is-selected": props.isSelected,
        "mark-correct": props.mark && props.mark === "correct",
        "mark-incorrect": props.mark && props.mark === "incorrect",
      })}
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
};

type SelectableTextProps = {
  data: StemTextSelectable;
};
const SelectableText = (props: SelectableTextProps) => {
  const [state, dispatch] = useContext(QuestionContext);

  // Reveal Key
  useEffect(() => {
    if (state.revealKey) {
      const keys: any[] = [];
      for (const i in props.data.scoring?.options) {
        if (props.data.scoring.options[i].isKey) {
          keys.push(i);
        }
      }

      dispatch({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            options: keys,
          },
        },
      });
    }
  }, [state.revealKey]);

  const handleTextClick = (textId: string) => {
    const rsp = produce(
      state.response?.[props.data.id]?.options || [],
      (draft: string[]) => {
        if (props?.data?.config?.allowMultipleKeys) {
          if (includes(draft, textId)) {
            pull(draft, textId); // remove selection from response
          } else {
            draft.push(textId); // add selection to response
          }
        } else {
          draft = [textId];
        }

        return draft;
      },
    );

    dispatch({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: {
          options: rsp,
        },
      },
    });
  };

  const htmlToReactParser = new Parser();
  const processNodeDefinitions = HtmlToReact.ProcessNodeDefinitions();
  const reactElement = htmlToReactParser.parseWithInstructions(
    props.data.content.text.body ?? "",
    () => true,
    [
      {
        shouldProcessNode: function (node: any) {
          return node.attribs && node.attribs["data-hottext-id"];
        },
        processNode: function (node: any, children, idx) {
          const gapId = node.attribs["data-hottext-id"];
          const gap = find(props.data.content.options, ["id", gapId]);
          const rsp = state.response?.[props.data.id]?.options || [];

          // Scoring
          let mark;
          const keys: any[] = [];
          const isSelected = rsp && rsp.includes(gapId);
          const scoringOptions = props.data.scoring?.options || {};
          for (const i in scoringOptions) {
            if (scoringOptions[i].isKey) {
              keys.push(i);
            }
          }

          if (state.scoreResponses) {
            if (isSelected) {
              mark = "incorrect";
              if (keys.includes(gapId)) {
                mark = "correct";
              }
            }
          }

          const gapProps: TextProps = {
            key: idx,
            data: gap,
            mark: mark,
            gapId: gapId,
            isSelected: rsp && rsp.includes(gapId) ? true : false,
            onTextClick: () => {
              handleTextClick(gapId);
            },
          };

          return React.createElement(Text, gapProps);
        },
      },
      {
        // pre-process MathML
        shouldProcessNode: (node: any) => node?.name === "math",
        processNode: function (node) {
          return <Math display={false} mathml={serializeNode(node)} />;
        },
      },
      {
        shouldProcessNode: function () {
          return true;
        },
        processNode: processNodeDefinitions.processDefaultNode,
      },
    ],
  );

  return (
    <div className="item-player-element item-player-stem">
      <Prompt data={props.data.content.prompt} />
      <ContentStyles>
        <div
          className="item-player-selectable-text"
          style={{ maxWidth: "80rem" }}
        >
          {reactElement}
        </div>
      </ContentStyles>
    </div>
  );
};
export default SelectableText;
